import React from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTitle, MetricCard, MetricGrid } from '@/nova/components';
import type { OverviewMetricVM } from '../../mapping';
import { OverviewCard, OverviewLayout, Col, AsideSlot, AsideBody } from './OverviewKpis.styles';

export type OverviewKpisProps = {
  metrics: OverviewMetricVM[];
  loading: boolean;
  /** Optional card rendered to the right of the KPI grid, stretched to its full height. */
  aside?: React.ReactNode;
};

/** "This month's Overview": the brand-wide affiliate KPIs, assembled from the reports. */
export const OverviewKpis = ({ metrics, loading, aside }: OverviewKpisProps): React.ReactElement => {
  const { t } = useTranslation();
  // Literal-keyed map so the i18n extractor harvests each key (a dynamic t(metric.label) would not extract).
  const labels: Record<string, string> = {
    'Total Spend': t('Total Spend'),
    'Blended CPA': t('Blended CPA'),
    Registrations: t('Registrations'),
    FTDs: t('FTDs'),
  };
  const grid = (
    <MetricGrid>
      {metrics.map((metric) => (
        // `raised` + `surface="panel"` give the acquisition performance-grid card look (a lifted
        // tile with a washed value box). `raised` is needed because these now sit inside the
        // OverviewCard (cardOuter), where the default featureCard fill would blend into it.
        <MetricCard
          key={metric.id}
          label={labels[metric.label] ?? metric.label}
          value={metric.value}
          loading={loading}
          surface="panel"
          raised
        />
      ))}
    </MetricGrid>
  );
  return (
    <OverviewCard>
      {aside ? (
        // Each half carries its own title so "This month's Overview" scopes to the KPIs and
        // "Your Affiliates" clearly labels the summary as a separate section.
        <OverviewLayout>
          <Col>
            <SectionTitle title={t("This month's Overview")} />
            {grid}
          </Col>
          <AsideSlot>
            <SectionTitle title={t('Your Affiliates')} />
            <AsideBody>{aside}</AsideBody>
          </AsideSlot>
        </OverviewLayout>
      ) : (
        <>
          <SectionTitle title={t("This month's Overview")} />
          {grid}
        </>
      )}
    </OverviewCard>
  );
};

export default OverviewKpis;
